import * as RadixAccordion from '@radix-ui/react-accordion';
import { forwardRef, Ref } from 'react';
import styled, { keyframes } from 'styled-components';
import { coreTokens } from '../../core';
import { Icon } from '../icon';
import { theme } from './accordion.theme';

const slideDown = keyframes`
  from {
    height: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
  }
`;

const slideUp = keyframes`
 from {
    height: var(--radix-accordion-content-height);
  }
  to {
    height: 0;
  }
`;

const StyledAccordionRoot = styled(RadixAccordion.Root)`
	width: 100%;
`;

const StyledAccordionItem = styled(RadixAccordion.Item)`
	user-select: none;
	-webkit-tap-highlight-color: transparent;
	-webkit-user-drag: none;
	-webkit-touch-callout: none;

	overflow: hidden;
	margin-top: 1px;
	border-bottom: 1px solid #b3b7be;
	padding: ${coreTokens.spacing.x1} 0;

	&:first-child {
		margin-top: 0;
	}
`;

const StyledAccordionHeader = styled(RadixAccordion.Header)`
	display: flex;
	font: ${theme.getValue('headerFont')};
`;

const StyledAccordionTrigger = styled(RadixAccordion.Trigger)`
	all: unset;
	font-family: inherit;
	background-color: transparent;
	height: ${coreTokens.size.x6};
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;

	&:hover {
		background-color: var(--mauve-2);
	}

	& > .accordionChevron {
		transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
	}

	&[data-state='open'] > .accordionChevron {
		transform: rotate(180deg);
	}

	&:focus-visible {
		position: relative;
		z-index: 1;
		box-shadow: 0 0 0 2px #44a5ff;
	}
`;

const StyledAccordionTriggerRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${coreTokens.spacing.x1};
	padding-right: ${coreTokens.spacing.x2};
	flex-shrink: 0;
	flex-grow: 1;
`;

const StyledAccordionContent = styled(RadixAccordion.Content)`
	overflow: hidden;
	font: ${theme.getValue('bodyFont')};

	&[data-state='open'] {
		animation: ${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1);
	}

	&[data-state='closed'] {
		animation: ${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1);
	}
`;

const StyledInnerContent = styled.div`
	padding: ${coreTokens.spacing.x1} 0;
`;

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for displaying a avatar.
 */

const AccordionTrigger = forwardRef(function AccordionTrigger(
	{ children, className, ...props }: RadixAccordion.AccordionTriggerProps,
	forwardedRef: Ref<HTMLButtonElement>,
): JSX.Element {
	return (
		<StyledAccordionHeader>
			<StyledAccordionTrigger className={className} {...props} ref={forwardedRef}>
				<StyledAccordionTriggerRow>{children}</StyledAccordionTriggerRow>
				<Icon type='chevronDown' className='accordionChevron' />
			</StyledAccordionTrigger>
		</StyledAccordionHeader>
	);
});

const AccordionContent = forwardRef(function AccordionContent(
	{ children, className, ...props }: RadixAccordion.AccordionContentProps,
	forwardedRef: Ref<HTMLDivElement>,
): JSX.Element {
	return (
		<StyledAccordionContent className={className} {...props} ref={forwardedRef}>
			<StyledInnerContent>{children}</StyledInnerContent>
		</StyledAccordionContent>
	);
});

type ItemProps = RadixAccordion.AccordionItemProps & {
	/**
	 * The row to display in the accordion item.
	 */
	header: React.ReactNode;

	/**
	 * The content to display inside the item.
	 */
	children: React.ReactNode;
};

function Item(props: ItemProps): JSX.Element {
	return (
		<StyledAccordionItem {...props}>
			<AccordionTrigger>{props.header}</AccordionTrigger>
			<AccordionContent>{props.children}</AccordionContent>
		</StyledAccordionItem>
	);
}

export const Accordion = {
	Root: StyledAccordionRoot,
	Item: Item,
} as const;
